import { parse } from "csv-parse/sync";
import chardet from "chardet";
import readXlsxFile, { Row } from "read-excel-file/browser";

export const EXCEL_EXTENSION = ".xlsx";
export const CSV_EXTENSION = ".csv";
export const IMAGE_EXTENSIONS = [".svg", ".png", ".jpg", ".jpeg"];
export const PDF_EXTENSION = ".pdf";

function countNullValues(row: Row): number {
  const nullCells = row.filter((cell) => cell === null);
  return nullCells.length;
}

export interface ColumnInfo {
  headerRowIndex: number;
  headers: string[];
}

function numEntriesInRow(row: string[]): number {
  return row.filter((cell) => cell.length > 0).length;
}

function getCSVColumns(csv: Buffer | string) {
  const csvSpreadsheet = parse(csv, {
    bom: true,
    skipRecordsWithError: true,
    relaxColumnCountMore: true,
  });
  const maxColumnCount = Math.max(
    ...csvSpreadsheet.map((row) => numEntriesInRow(row)),
  );
  const headerRowIndex = csvSpreadsheet.findIndex(
    (row) => numEntriesInRow(row) == maxColumnCount,
  );
  if (headerRowIndex >= 0) {
    const headerRow = csvSpreadsheet[headerRowIndex]!;
    return {
      headerRowIndex,
      headers: headerRow.filter((columnName) => columnName.length > 0),
    };
  }
  return null;
}

export function getCSVData(csv: Buffer | string) {
  const csvSpreadsheet = parse(csv, {
    bom: true,
    skipRecordsWithError: true,
    relaxColumnCountMore: true,
  });
  const maxColumnCount = Math.max(
    ...csvSpreadsheet.map((row) => numEntriesInRow(row)),
  );
  const headerRowIndex = csvSpreadsheet.findIndex(
    (row) => numEntriesInRow(row) == maxColumnCount,
  );
  if (headerRowIndex >= 0) {
    const headerRow = csvSpreadsheet[headerRowIndex]!;
    return {
      headerRowIndex,
      numColumns: maxColumnCount,
      headers: headerRow.filter((columnName) => columnName.length > 0),
      data: csvSpreadsheet.filter((row, index) => index !== headerRowIndex),
    };
  }
  return null;
}

export async function getFileColumns(
  filename: string,
  file: Blob,
  onError?: (errorMessage: string) => Promise<void> | void,
): Promise<ColumnInfo | null> {
  if (filename.endsWith(EXCEL_EXTENSION)) {
    try {
      const excelSpreadsheet = (await readXlsxFile(file))[0];
      if (excelSpreadsheet !== undefined) {
        const minNullCount = Math.min(
          ...excelSpreadsheet.data.map(countNullValues),
        );
        const headerRowIndex = excelSpreadsheet.data.findIndex(
          (row) => countNullValues(row) === minNullCount,
        );
        if (headerRowIndex >= 0) {
          return {
            headerRowIndex,
            headers: excelSpreadsheet.data[headerRowIndex]!.map((cell) => {
              if (cell === null) {
                return "";
              }
              return cell.toString();
            }).filter((columnName) => columnName.length > 0),
          };
        }
      }
    } catch {
      await onError?.(`Failed to parse xlsx file ${filename}`);
      return null;
    }
  } else {
    try {
      const uint8Array = new Uint8Array(await file.arrayBuffer());
      const encoding = chardet.detect(uint8Array);
      if (encoding !== null) {
        return getCSVColumns(Buffer.from(uint8Array));
      } else {
        await onError?.("Failed to parse CSV. Please try with an xlsx file.");
        return null;
      }
    } catch {
      try {
        return getCSVColumns(await file.text());
      } catch {
        await onError?.("Failed to parse CSV. Please try with an xlsx file.");
        return null;
      }
    }
  }
  return null;
}
